import { InboxEntry, TaskSubgroup } from '@this+that/models';
import {
  generateKeyBetween,
  generateNKeysBetween,
} from 'jittered-fractional-indexing';

const ORDER_KEY_CHARSET_PATTERN = /^[0-9A-Za-z]+$/;

// Comparator return values: negative = a before b, positive = a after b.
const A_BEFORE_B = -1;
const A_AFTER_B = 1;
const EQUAL = 0;

// Codepoint comparison, NOT localeCompare: fractional-indexing keys and the
// DynamoDB GSI both order by ASCII/UTF-8 bytes, where 'B' < 'a'. localeCompare
// would flip mixed-case keys and desync client order from the GSI.
const compareCodepoints = (a: string, b: string): number => {
  if (a === b) {
    return EQUAL;
  }
  return a < b ? A_BEFORE_B : A_AFTER_B;
};

export const generateOrderKeyBetween = (
  a: string | null,
  b: string | null,
): string => generateKeyBetween(a, b);

export const generateOrderKeysBetween = (
  a: string | null,
  b: string | null,
  n: number,
): string[] => generateNKeysBetween(a, b, n);

export const isValidOrderKey = (key: string): boolean => {
  if (!key || !ORDER_KEY_CHARSET_PATTERN.test(key)) {
    return false;
  }

  try {
    generateKeyBetween(key, null);
    return true;
  } catch {
    return false;
  }
};

type ManualOrderable = Pick<InboxEntry, 'orderKey' | 'id' | 'createdAt'>;

export const compareEntriesManual = (
  a: ManualOrderable,
  b: ManualOrderable,
): number => {
  if (a.orderKey && b.orderKey) {
    return a.orderKey === b.orderKey
      ? compareCodepoints(a.id, b.id)
      : compareCodepoints(a.orderKey, b.orderKey);
  }

  if (a.orderKey && !b.orderKey) {
    return A_BEFORE_B;
  }

  if (!a.orderKey && b.orderKey) {
    return A_AFTER_B;
  }

  const aCreatedAt = a.createdAt ?? 0;
  const bCreatedAt = b.createdAt ?? 0;
  return aCreatedAt === bCreatedAt
    ? compareCodepoints(a.id, b.id)
    : aCreatedAt - bCreatedAt;
};

type TaskSubgroupOrderable = Pick<TaskSubgroup, 'orderKey' | 'id'>;

export const compareTaskSubgroups = (
  a: TaskSubgroupOrderable,
  b: TaskSubgroupOrderable,
): number => {
  if (a.orderKey && b.orderKey) {
    return a.orderKey === b.orderKey
      ? compareCodepoints(a.id, b.id)
      : compareCodepoints(a.orderKey, b.orderKey);
  }

  if (a.orderKey && !b.orderKey) {
    return A_BEFORE_B;
  }

  if (!a.orderKey && b.orderKey) {
    return A_AFTER_B;
  }

  return compareCodepoints(a.id, b.id);
};
